import { getConfig, getCurrencyCode } from "@/features/branding";
import { downloadXlsxFile } from "@/features/custom_downloads";
import { PortfolioExportAssetLevelDownloadButton } from "@/features/pe_aldd";
import { RemoteJobActionsProps } from "@/features/remote_jobs";
import { logToolUsage } from "@/features/tracking";
import { store } from "@/store";
import { useGetGroupsQuery } from "@/store/services/supabase";
import { portfolioExportActions } from "@/store/slices/portfolioExport";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { downloadPortfolioSummary, enqueuePortfolioSummary } from "@/tools/aggregate/portfolio-export/utils/stream";
import { downloadLargeSiteSummary, enqueueLargeSiteSummary } from "@/tools/risk-bands/large-site-screen/utils/stream";
import { useIsOwner } from "@/utils/hooks/useIsOwner";
import { useKeys } from "@/utils/hooks/useKeys";
import { CheckCircleOutlined, CloseCircleOutlined, CloudDownloadOutlined, EyeOutlined, FileAddOutlined, LoadingOutlined, MoreOutlined, StopOutlined } from "@ant-design/icons";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import * as Sentry from "@sentry/nextjs";
import { useUser } from "@supabase/auth-helpers-react";
import { Button, ButtonProps, Dropdown, Input, Modal, notification, Select, Switch } from "antd";
import { MenuProps } from "antd/lib";
import { useRouter } from "next/router";
import React, { useState } from "react";

/**
 *
 */
interface ActionButtonProps extends ButtonProps {
  isRaw?: boolean;
}
export type PortfolioExportJobActionsProps = Omit<RemoteJobActionsProps<PortfolioExportJobSchema>, "status" | "completedActions">;
type OnEditProps = {
  schema: PortfolioExportJobSchema;
  name: string;
  isPrivate: boolean;
  groupId: string;
};
export const PortfolioExportJobActions = (props: PortfolioExportJobActionsProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const isOwner = useIsOwner();
  const [modal, modalContext] = Modal.useModal();
  const router = useRouter();
  const [key] = useKeys();
  const buttons: (ActionButtonProps | undefined)[] = [];
  const generateSummary = async () => {
    console.log("generateSummary.schema: ", props.schema);
    const tool = props.schema.isLargeSite ? "largeSite" : "portfolioInsights";
    setIsGenerating(true);
    if (!key.user) return;
    try {
      if (props.schema.isLargeSite) {
        await enqueueLargeSiteSummary(props.schema.id, props.schema.summaryId, props.schema.secret, props.schema.stamps, props.schema.metadata);
      } else {
        await enqueuePortfolioSummary(props.schema.id, props.schema.summaryId, props.schema.secret, props.schema.stamps);
      }
      logToolUsage({
        id: `comprehensive.${tool}.summary`,
        count: props.schema.assets.totalCount,
        portfolioId: props.schema.id,
        type: `${props.schema.isLargeSite ? "large_site" : "portfolio_insights"}:summary`
      });
      setIsGenerating(false);
    } catch (err) {
      console.error("Something went wrong while generating portfolio");
      const message = err instanceof Error ? err.message : "";
      notification.open({
        duration: 0,
        message: <div className="flex gap-2 font-semibold text-red-500">
            <CloseCircleOutlined />
            <span>Summary Failed</span>
          </div>,
        description: <div className="text-sm">
            Error while generating &quot;<em>{props.schema.name}</em>&quot;. Please try again or restart the run. If the
            issue persists, contact support.
            <br />
            {message}
          </div>
      });
      Sentry.captureException(`Summary Request Error: ${err}`, {
        tags: {
          section: "Summary Request"
        },
        extra: {
          userId: props.schema.userId,
          portfolioId: props.schema.id,
          groupId: props.schema.groupId,
          summaryId: props.schema.summaryId
        }
      });
      setIsGenerating(false);
    }
  };
  const downloadSummary = async () => {
    setIsDownloading(true);
    const currency_code = getCurrencyCode();
    const settings = currency_code ? {
      currency_code
    } : undefined;
    let buffer;
    try {
      if (props.schema.isLargeSite) {
        buffer = await downloadLargeSiteSummary(props.schema.id, props.schema.summaryId, props.schema.secret, props.schema.stamps, props.schema.metadata, currency_code);
      } else {
        buffer = await downloadPortfolioSummary(props.schema.summaryId, settings);
      }
      if (buffer) {
        const suffix = props.schema.isLargeSite ? "large_site_summary" : "summary";
        const tool = props.schema.isLargeSite ? "largeSite" : "portfolioInsights";
        downloadXlsxFile(buffer, {
          filename: `${props.schema.name}-${suffix}.xlsx`
        });
        notification.open({
          duration: 0,
          message: <div className="flex gap-2">
              <CheckCircleOutlined />
              <span>Success</span>
            </div>,
          description: <div className="text-sm">
              Your summary &quot;<em>{props.schema.name}</em>&quot; has been successfully downloaded.
            </div>
        });

        // Log to HubSpot when this has completed.
        logToolUsage({
          id: `comprehensive.${tool}.summary`,
          count: props.schema.assets.totalCount,
          portfolioId: props.schema.id,
          type: `${props.schema.isLargeSite ? "large_site" : "portfolio_insights"}:summary`
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      notification.open({
        duration: 0,
        message: <div className="flex gap-2 font-semibold text-red-500">
            <CloseCircleOutlined />
            <span>Summary Failed</span>
          </div>,
        description: <div className="text-sm">
            There was a problem downloading &quot;<em>{props.schema.name}</em>&quot;. Please try again. If the issue
            persists, contact support.
            <br />
            {message}
          </div>
      });
      Sentry.captureException(`Summary Download Error: ${err}`, {
        tags: {
          section: "Summary Download"
        },
        extra: {
          userId: props.schema.userId,
          portfolioId: props.schema.id,
          groupId: props.schema.groupId,
          summaryId: props.schema.summaryId
        }
      });
    }
    setIsDownloading(false);
  };
  const onDeleteJob = async (name: string, action: "delete" | "cancel" = "delete") => {
    const content = action === "delete" ? "Deleting this will permanently remove it for all members of your organisational group. Are you sure you want to continue?" : `Are you sure you want to cancel ${name}?`;
    const confirmed = await modal.confirm({
      content,
      okText: "Ok"
    });
    if (confirmed) {
      props.onDeleteJob();
    }
  };
  const handleSummaryOpen = () => {
    store.dispatch(portfolioExportActions.setSummaryStatusModal({
      isOpen: true,
      highlightedSummaryId: props.schema.summaryId
    }));
  };
  const isSummaryProcessing = !!props.schema.summaryId && !props.schema.isSummaryGenerated;
  const isSummaryGenerated = !!props.schema.summaryId && props.schema.isSummaryGenerated;
  const buttonsDisabled = !props.schema.assets.processedCount;
  switch (props.schema.status) {
    case "completed_with_errors":
    case "completed":
      if (isSummaryProcessing) {
        buttons.push({
          icon: <LoadingOutlined />,
          children: <span>Generating Summary</span>,
          onClick: handleSummaryOpen,
          className: "!font-normal"
        });
      } else {
        if (isSummaryGenerated) {
          buttons.push({
            loading: isDownloading,
            disabled: isDownloading || buttonsDisabled,
            icon: <CloudDownloadOutlined />,
            children: "Download Summary",
            onClick: downloadSummary,
            className: "!font-normal"
          });
        } else {
          buttons.push({
            loading: isGenerating,
            disabled: isGenerating || buttonsDisabled,
            icon: <FileAddOutlined />,
            children: "Generate Summary",
            onClick: generateSummary,
            className: "!font-normal"
          });
        }
      }
      buttons.push({
        children: <PortfolioExportAssetLevelDownloadButton schema={props.schema} disabled={buttonsDisabled} />,
        isRaw: true
      });
      if (getConfig().pages.collections.enabled) {
        buttons.push({
          icon: <EyeOutlined />,
          children: "View On Map",
          disabled: props.schema.assets.totalCount > 2500 || buttonsDisabled,
          className: "!font-inherit",
          onClick: () => {
            router.push((props.schema.isLargeSite ? "/large-sites/" : "/portfolios/") + props.schema.id);
          },
          title: props.schema.assets.totalCount > 2500 ? `Can only view up to 2500 ${props.schema.isLargeSite ? "points" : "assets"} on map` : undefined
        });
      }
      break;
    case "error":
      buttons.push({
        icon: <DeleteOutlined />,
        children: "Delete",
        onClick: async () => await onDeleteJob(props.schema.name)
      });
      break;
    default:
      buttons.push({
        icon: <StopOutlined />,
        children: "Cancel",
        onClick: async () => await onDeleteJob(props.schema.name, "cancel")
      });
  }
  const dropdownMenuItems: MenuProps["items"] = [{
    key: "edit-portfolio",
    label: "Edit...",
    onClick: async () => setShowEditModal(true)
  }, {
    key: "delete-portfolio",
    label: "Delete",
    onClick: async () => await onDeleteJob(props.schema.name),
    icon: <DeleteOutlined />
  }];
  const handleEditOk = async (val: OnEditProps) => {
    if (props.onEditJob) {
      await props.onEditJob(val);
    }
  };
  return <div style={{
    display: "flex",
    gap: "0.25rem",
    alignItems: "center",
    fontWeight: 400
  }} data-sentry-component="PortfolioExportJobActions" data-sentry-source-file="PortfolioExportJobActions.tsx">
      {modalContext}

      {buttons.map((button, index) => {
      if (!button) {
        return undefined;
      }
      if (button.isRaw) {
        return <React.Fragment key={index}>{button.children}</React.Fragment>;
      }
      return <Button key={index} size="small" style={{
        fontWeight: "inherit"
      }} {...button} />;
    })}

      <Dropdown trigger={["click"]} disabled={!["completed", "completed_with_errors"].includes(props.schema.status)} menu={{
      items: dropdownMenuItems
    }} overlayStyle={{
      width: 160
    }} data-sentry-element="Dropdown" data-sentry-source-file="PortfolioExportJobActions.tsx">
        <Button size="small" icon={<MoreOutlined />} style={{
        fontWeight: "inherit"
      }} type="text" data-sentry-element="Button" data-sentry-source-file="PortfolioExportJobActions.tsx" />
      </Dropdown>

      {props.onEditJob && <EditJob open={showEditModal} isOwner={isOwner || false} schema={props.schema} onCancel={() => setShowEditModal(false)} onOk={handleEditOk} hasOrgKeys={!!key.orgSecret} />}
    </div>;
};
const EditJob = ({
  schema,
  isOwner,
  open,
  onCancel,
  onOk,
  hasOrgKeys
}: {
  schema: PortfolioExportJobSchema;
  isOwner: boolean;
  open: boolean;
  onCancel: () => void;
  onOk: (props: OnEditProps) => Promise<void>;
  hasOrgKeys: boolean;
}) => {
  const {
    data: groups
  } = useGetGroupsQuery();
  const user = useUser();
  const [name, setName] = useState(schema.name);
  const [isPrivate, setIsPrivate] = useState(schema.isPrivate || false);
  const [groupId, setGroupId] = useState(schema.groupId);
  if (!groups) return <></>;
  const groupOpts = groups.map(g => ({
    key: g.id,
    value: g.id,
    label: g.name || ""
  }));
  const handleOk = async () => {
    console.log("ok");
    await onOk({
      schema,
      groupId,
      name,
      isPrivate
    }).then(onCancel);
  };
  return <Modal title={`Edit ${schema.name}`} open={open} onCancel={onCancel} onOk={handleOk} okText="Save" data-sentry-element="Modal" data-sentry-component="EditJob" data-sentry-source-file="PortfolioExportJobActions.tsx">
      <div className="grid grid-cols-4 items-center gap-y-2 py-4">
        <div>Name</div>
        <Input value={name} className="col-span-3" onChange={val => setName(val.target.value)} data-sentry-element="Input" data-sentry-source-file="PortfolioExportJobActions.tsx" />

        {user?.id === schema.userId && <>
            <div>Private</div>
            <div className="col-span-3">
              <Switch value={isPrivate} onChange={() => setIsPrivate(!isPrivate)} disabled={!hasOrgKeys} />
            </div>
            <div className="col-span-3 col-start-2 h-4 text-xs text-gray-500">
              {isPrivate ? "Your portfolio will be only visible to you." : "Your portfolio will be visible to everyone in the group."}
            </div>
          </>}

        {!isPrivate && <>
            <div>Share with</div>
            <Select placeholder="Select group..." options={groupOpts} className="col-span-3" onChange={e => setGroupId(e)} value={groupId} disabled={!isOwner} />
          </>}
      </div>
    </Modal>;
};